import React from 'react';
import styled from 'styled-components';
import { Bell, Check, CheckCircle } from 'lucide-react';
import { Reminder } from '../../services/reminderService';

interface UrgentTasksListProps {
  reminders: Reminder[];
  onCompleteReminder: (id: number) => void;
  onViewAll?: () => void;
}

const Container = styled.div`
  padding: 20px;
  /* No box styling - unified canvas */

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TitleIcon = styled.span`
  color: var(--text-secondary);
  display: flex;
  align-items: center;
`;

const UrgentBadge = styled.span`
  background: #dc2626;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
`;

const ViewAllButton = styled.button`
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 6px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const TasksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0 -20px;

  @media (max-width: 767px) {
    margin: 0 -16px;
  }
`;

const TaskCard = styled.div<{ $priority: 'high' | 'medium' | 'low' }>`
  padding: 12px 20px;
  border-right: 3px solid ${props => {
    switch (props.$priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#d97706';
      case 'low': return '#16a34a';
      default: return 'var(--primary-color)';
    }
  }};
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    padding: 12px 16px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const TaskContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const TaskTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
  word-break: break-word;
`;

const TaskMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const MetaBadge = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$color ? `${props.$color}15` : 'var(--background-secondary)'};
  color: ${props => props.$color || 'var(--text-secondary)'};
  border: 1px solid ${props => props.$color ? `${props.$color}40` : 'transparent'};
`;

const TaskDescription = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TaskActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;
    justify-content: stretch;

    button {
      flex: 1;
    }
  }
`;

const CompleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  background: #16a34a;
  color: white;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #15803d;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 20px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const EmptyIconWrapper = styled.div`
  color: #16a34a;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const UrgentTasksList: React.FC<UrgentTasksListProps> = ({
  reminders,
  onCompleteReminder,
  onViewAll
}) => {
  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffMs < 0) {
      return 'פג תוקף';
    }

    if (diffHours < 1) {
      return `בעוד ${diffMinutes} דקות`;
    }

    if (diffHours < 24) {
      return `בעוד ${diffHours} שעות`;
    }

    return date.toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityLevel = (priority: number): 'high' | 'medium' | 'low' => {
    switch (priority) {
      case 1: return 'high';
      case 2: return 'medium';
      case 3: return 'low';
      default: return 'medium';
    }
  };

  const getPriorityColor = (priority: number): string => {
    switch (priority) {
      case 1: return '#dc2626';
      case 2: return '#d97706';
      case 3: return '#16a34a';
      default: return 'var(--text-secondary)';
    }
  };

  // Show max 5 urgent reminders
  const displayReminders = reminders.slice(0, 5);
  const hasUrgent = reminders.length > 0;

  return (
    <Container>
      <Header>
        <Title>
          <TitleIcon><Bell size={16} strokeWidth={1.5} /></TitleIcon>
          תזכורות דחופות להיום
          {hasUrgent && <UrgentBadge>{reminders.length}</UrgentBadge>}
        </Title>
        {onViewAll && reminders.length > 0 && (
          <ViewAllButton onClick={onViewAll}>
            כל התזכורות
          </ViewAllButton>
        )}
      </Header>

      {displayReminders.length === 0 ? (
        <EmptyState>
          <EmptyIconWrapper><CheckCircle size={40} strokeWidth={1.5} /></EmptyIconWrapper>
          <div>אין תזכורות דחופות להיום</div>
        </EmptyState>
      ) : (
        <TasksList>
          {displayReminders.map(reminder => (
            <TaskCard key={reminder.id} $priority={getPriorityLevel(reminder.priority)}>
              <TaskContent>
                <TaskTitle>{reminder.title}</TaskTitle>
                <TaskMeta>
                  <MetaBadge $color={getPriorityColor(reminder.priority)}>
                    {reminder.priorityName}
                  </MetaBadge>
                  <MetaBadge>{reminder.reminderTypeName}</MetaBadge>
                  <MetaBadge>{formatDateTime(reminder.dueDateTime)}</MetaBadge>
                  {reminder.isOverdue && (
                    <MetaBadge $color="#dc2626">פג תוקף</MetaBadge>
                  )}
                </TaskMeta>
                {reminder.description && (
                  <TaskDescription title={reminder.description}>
                    {reminder.description}
                  </TaskDescription>
                )}
              </TaskContent>
              <TaskActions>
                <CompleteButton onClick={() => onCompleteReminder(reminder.id)}>
                  <Check size={14} strokeWidth={2} />
                  סיים
                </CompleteButton>
              </TaskActions>
            </TaskCard>
          ))}
        </TasksList>
      )}
    </Container>
  );
};

export default UrgentTasksList;
